import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (attempts >= 8) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="App bg-brand-dark min-h-screen flex items-center justify-center px-6">
      <div className="noise-overlay" />
      <div className="relative max-w-xl w-full text-center rounded-sm border border-brand-gold/30 p-10 md:p-14" data-testid="payment-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-brand-gold mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl text-brand-mist mb-3" data-testid="payment-checking-heading">Confirming your payment…</h1>
            <p className="font-sans text-base text-brand-mist/60">One moment. Don't close this page.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-brand-tiffanydeep mx-auto mb-6" />
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-mist mb-4" data-testid="payment-paid-heading">
              Payment received. <span className="italic text-brand-gold">The work begins.</span>
            </h1>
            <p className="font-sans text-base text-brand-mist/70 leading-relaxed mb-8">
              You're booked. Brandi Jo will reach out shortly with your written pre-work.
              Complete it by hand. The three hours start deeper because of it.
            </p>
            <Link to="/" className="inline-flex font-sans text-base bg-brand-gold text-white px-8 py-4 rounded-full hover:bg-brand-tiffany transition-colors" data-testid="payment-success-home-btn">
              Back to Home
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-brand-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-brand-mist mb-3">Payment processing</h1>
            <p className="font-sans text-base text-brand-mist/60">
              Your payment is still being confirmed. You'll receive an email receipt from Stripe once it clears.
              If anything looks off, email <a href="mailto:me@brandijo.com" className="underline text-brand-gold">me@brandijo.com</a>.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-brand-mist mb-3" data-testid="payment-error-heading">Something went wrong</h1>
            <p className="font-sans text-base text-brand-mist/60 mb-8">
              We couldn't confirm this payment. If you were charged, email{" "}
              <a href="mailto:me@brandijo.com" className="underline text-brand-gold">me@brandijo.com</a>.
            </p>
            <Link to="/the-session" className="inline-flex font-sans text-base bg-brand-gold text-white px-8 py-4 rounded-full hover:bg-brand-tiffany transition-colors">
              Back to The Session
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="App bg-brand-dark min-h-screen flex items-center justify-center px-6">
      <div className="noise-overlay" />
      <div className="relative max-w-xl w-full text-center rounded-sm border border-brand-gold/30 p-10 md:p-14" data-testid="payment-cancel-card">
        <h1 className="font-serif text-3xl sm:text-4xl text-brand-mist mb-4" data-testid="payment-cancel-heading">
          Checkout <span className="italic text-brand-gold">cancelled.</span>
        </h1>
        <p className="font-sans text-base text-brand-mist/70 leading-relaxed mb-8">
          No charge was made. The resistance you just felt? That's the material. Bring it.
        </p>
        <Link to="/the-session" className="inline-flex font-sans text-base bg-brand-gold text-white px-8 py-4 rounded-full hover:bg-brand-tiffany transition-colors" data-testid="payment-cancel-back-btn">
          Back to The Session
        </Link>
      </div>
    </div>
  );
}
